import { Fragment, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTranslation } from 'react-i18next';
import { displayFieldName } from './fields';
import { MAX_COLUMN_GROUPS, type PivotModel, type PivotRow, type ResultColumn } from './pivot';

const NO_COLUMN: ResultColumn = { key: '', label: '', type: 'STRING', role: 'FIELD', index: 0 };

export interface PivotGridProps {
  readonly model: PivotModel;
  /** A cell as the viewer shows it: the report's format over the workspace defaults (F-1…F-3). */
  readonly formatCell: (column: ResultColumn, raw: string | null) => string;
  /** The table's accessible name — the report's name. */
  readonly ariaLabel: string;
  /** The field's title in the report (`label` override, D-4); default the field name shown. */
  readonly titleOf?: (column: ResultColumn) => string;
}

/**
 * The pivot table of a report (SDD-024 P-3, P-4; mockup boards 3 and 4) — composed of MUI Table
 * rows, never a grid library: row headers indented by level with their subtotals above their
 * children, one column group per value of the column fields plus "Gesamt", the values side by side
 * inside a group. The service sorts; this never re-sorts (P-6). Every cell names its row path, its
 * column group and its value in `data-*` attributes, so a scenario can address "the ACTIVE row
 * under Kfz-Flotte, column company" without counting positions.
 */
export function PivotGrid({ model, formatCell, ariaLabel, titleOf }: PivotGridProps): ReactNode {
  const { t } = useTranslation('patterns');
  const title = (column: ResultColumn): string =>
    titleOf?.(column) ?? displayFieldName(column.label);
  const values = model.values;
  const hasColumnFields = model.columnFields.length > 0;
  const groupLabel = (values: readonly (string | null)[], total: boolean): string =>
    total
      ? t('reporting.pivot.total')
      : values.map((v, i) => formatCell(model.columnFields[i] ?? NO_COLUMN, v)).join(' · ');
  const rowHeader = model.rowFields.map(title).join(' / ');
  const rowLabel = (row: PivotRow): string => {
    if (row.total) return t('reporting.pivot.total');
    const field = model.rowFields[row.depth - 1];
    return field === undefined ? '' : formatCell(field, row.path[row.depth - 1] ?? null);
  };
  const pathLabels = (row: PivotRow): string[] =>
    row.path.map((v, i) => formatCell(model.rowFields[i] ?? NO_COLUMN, v));
  const twoHeaderRows = hasColumnFields;

  return (
    <Box>
      {model.columnsCut && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('reporting.pivot.columnsCut', { count: MAX_COLUMN_GROUPS })}
        </Alert>
      )}
      <TableContainer sx={{ maxHeight: 640 }} tabIndex={0}>
        <Table size="small" stickyHeader aria-label={ariaLabel} data-testid="pivot-grid">
          <TableHead>
            {twoHeaderRows && (
              <TableRow>
                <TableCell rowSpan={2} sx={{ fontWeight: 600, verticalAlign: 'bottom' }}>
                  {rowHeader}
                </TableCell>
                {model.columnGroups.map((group) => (
                  <TableCell
                    key={group.key}
                    colSpan={values.length}
                    align="center"
                    sx={{ fontWeight: 600, borderLeft: 1, borderLeftColor: 'divider' }}
                    data-column-group={groupLabel(group.values, group.total)}
                  >
                    {groupLabel(group.values, group.total)}
                  </TableCell>
                ))}
              </TableRow>
            )}
            <TableRow>
              {!twoHeaderRows && <TableCell sx={{ fontWeight: 600 }}>{rowHeader}</TableCell>}
              {model.columnGroups.map((group) =>
                values.map((value, index) => (
                  <TableCell
                    key={`${group.key}:${value.key}`}
                    align="right"
                    sx={{
                      fontWeight: 600,
                      ...(index === 0 && twoHeaderRows
                        ? { borderLeft: 1, borderLeftColor: 'divider' }
                        : {}),
                    }}
                  >
                    {title(value)}
                  </TableCell>
                )),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {model.rows.map((row) => {
              const labels = pathLabels(row);
              return (
                <TableRow
                  key={row.key}
                  data-row-label={rowLabel(row)}
                  data-row-parent={row.depth > 1 ? (labels[row.depth - 2] ?? '') : ''}
                  data-row-path={JSON.stringify(labels)}
                  data-subtotal={row.subtotal ? 'true' : 'false'}
                  sx={row.subtotal ? { bgcolor: 'action.hover' } : undefined}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      pl: 1.5 + Math.max(0, row.depth - 1) * 2,
                      fontWeight: row.subtotal ? 600 : 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {rowLabel(row)}
                  </TableCell>
                  {model.columnGroups.map((group) => (
                    <Fragment key={group.key}>
                      {values.map((value, index) => {
                        const raw = row.cells[group.key]?.[index] ?? null;
                        const text =
                          row.cells[group.key] === undefined
                            ? t('reporting.pivot.empty')
                            : formatCell(value, raw);
                        return (
                          <TableCell
                            key={value.key}
                            align="right"
                            data-column-group={
                              hasColumnFields ? groupLabel(group.values, group.total) : title(value)
                            }
                            data-value={title(value)}
                            sx={{
                              whiteSpace: 'nowrap',
                              fontVariantNumeric: 'tabular-nums',
                              fontWeight: row.subtotal ? 600 : 400,
                              ...(index === 0 && twoHeaderRows
                                ? { borderLeft: 1, borderLeftColor: 'divider' }
                                : {}),
                            }}
                          >
                            {text}
                          </TableCell>
                        );
                      })}
                    </Fragment>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
